import { seatLimitDetail } from "@/components/SeatLimitDialog";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, HardHat, Lock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AcceptInvite() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const { setSession } = useAuth();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    if (!token) { setErr("Missing invite token."); return; }
    (async () => {
      try {
        const r = await api.get(`/invites/by-token/${token}`);
        if (!cancelled) setInvite(r.data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Invite invalid or expired.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invites/accept", { token, password });
      if (setSession) setSession(data);
      toast.success(`Welcome to ${invite.org_name}, ${data.name}!`);
      nav("/app");
    } catch (e) {
      setErr(seatLimitDetail(e) ? "This organisation has reached its active-user limit. Ask your company admin to review the plan, then retry this invitation before it expires." : e.response?.data?.detail || "Could not accept invite.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F4F4F5]">
      <div className="w-full max-w-md bg-white border-4 border-[#09090B]" data-testid="accept-invite-page">
        <div className="bg-[#09090B] p-4 flex items-center gap-2 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FF5F15] flex items-center justify-center">
              <HardHat className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black">FIELDLINK<span className="text-[#FF5F15]">.CO.NZ</span></span>
          </Link>
        </div>
        <div className="p-8">
          {err && !invite ? (
            <>
              <div className="w-12 h-12 bg-red-100 border-2 border-red-500 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-700" />
              </div>
              <h1 className="font-display text-2xl font-black uppercase tracking-tight">Invite unavailable</h1>
              <p className="text-sm text-zinc-600 mt-3">{err}</p>
              <Link to="/login" className="block mt-6">
                <Button className="w-full h-11 rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase font-bold tracking-wider">Go to sign in</Button>
              </Link>
            </>
          ) : !invite ? (
            <p className="text-sm text-zinc-500">Loading…</p>
          ) : (
            <form onSubmit={submit}>
              <div className="label-tag">/ joining the crew</div>
              <h1 className="font-display text-2xl font-black uppercase tracking-tight mt-2">You&rsquo;re invited.</h1>
              <p className="text-sm text-zinc-700 mt-3">
                <b>{invite.invited_by_name}</b> has invited <b>{invite.name}</b> to join <b>{invite.org_name}</b> as a <b>{invite.role}</b>.
              </p>
              <p className="text-xs text-zinc-500 mt-2 font-mono">{invite.email}</p>

              <div className="mt-6">
                <Label className="label-tag">Set your password</Label>
                <div className="relative mt-2">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input type="password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 pl-9 rounded-sm border-zinc-400" placeholder="••••••••" data-testid="accept-password-input" />
                </div>
              </div>
              <div className="mt-4">
                <Label className="label-tag">Confirm password</Label>
                <div className="relative mt-2">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 pl-9 rounded-sm border-zinc-400" placeholder="••••••••" data-testid="accept-confirm-input" />
                </div>
              </div>
              {err && <div className="mt-4 border border-red-300 bg-red-50 text-red-700 p-3 text-sm" data-testid="accept-error">{err}</div>}
              <Button type="submit" disabled={busy} className="w-full h-12 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase font-bold tracking-wider mt-6" data-testid="accept-submit-button">
                {busy ? "Setting up…" : <>Join {invite.org_name} <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
